import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geely/ui";
import { Pencil, Trash2, Calendar } from 'lucide-react';
import type { SpecialDateOperatingHours, Store } from '@geely/shared/types';
import { formatDate, getDayOfWeekLabel } from '@/lib/utils';

interface SpecialDateHoursTableProps {
  hours: SpecialDateOperatingHours[];
  userStore: Store | null;
  onEdit: (hours: SpecialDateOperatingHours) => void;
  onDelete: (id: string) => void;
}

export function SpecialDateHoursTable({ hours, userStore, onEdit, onDelete }: SpecialDateHoursTableProps) {
  // Sort by date (newest first)
  const sortedHours = [...hours].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Datas Especiais</CardTitle>
      </CardHeader>
      <CardContent>
        {hours.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="size-12 mx-auto mb-3 text-gray-400" />
            <p>Nenhuma data especial cadastrada.</p>
            <p className="text-sm mt-1">Cadastre exceções como feriados e expedientes especiais.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Dia da Semana</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>GCW</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead>Intervalo Almoço</TableHead>
                <TableHead>Intervalo</TableHead>
                <TableHead>Atend. Simultâneos</TableHead>
                <TableHead>Ativo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedHours.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-medium">{formatDate(h.date)}</TableCell>
                  <TableCell>{getDayOfWeekLabel(h.date)}</TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                        Fechado
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        Aberto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isGeelyCareWeekend ? (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300">Sim</Badge>
                    ) : (
                      <span className="text-gray-500">Não</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : (
                      <span>{h.startTime} - {h.endTime}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : h.hasLunchBreak ? (
                      <span className="text-sm">
                        {h.lunchStartTime} - {h.lunchEndTime}
                      </span>
                    ) : (
                      <span className="text-gray-500">Não</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : (
                      <span>{h.timeInterval} min</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isClosed ? (
                      <span className="text-gray-500">—</span>
                    ) : h.parallelAppointments !== undefined && h.parallelAppointments !== null ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {h.parallelAppointments} (específico)
                      </Badge>
                    ) : (
                      <span className="text-gray-500">{userStore?.parallelAppointments} (padrão)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {h.isActive ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300">Sim</Badge>
                    ) : (
                      <Badge variant="outline">Não</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(h)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(h.id)}
                      >
                        <Trash2 className="size-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
